import React, { useReducer, useContext, useCallback } from "react"
import AccountReducer from 'accounts/state/AccountReducer'
import { Commands } from 'shared/resources/BitbiteCommands';
import { SessionContext } from 'shared/system-controls/session/SessionProvider';
import UtilityAPI from "shared/utils/UtilityAPI";
import moment from 'moment-jalaali';
import { LanguageContext } from "shared/i18n/LanguageContext";

export const AccountContext = React.createContext();


const AccountState = ({ children }) => {
	const initialState = {
		loading: false,
	}
	const [state, dispatch] = useReducer(AccountReducer, initialState);
	const { asyncServiceCall } = useContext(SessionContext);
	const { i18n } = useContext(LanguageContext);

	//--------------------------------------------
	const getProvinces = useCallback(async () => {
		try {
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_GET_PROVINCES, {});
			dispatch({ type: 'SET_STATE_DATA', payload: { provinces: res } });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getAudits = useCallback(async (props) => {
		try {
			let audits = props.audits;
			props?.result && !props.audits?.length && dispatch({ type: 'SET_STATE_DATA', payload: { [props?.result]: [] } });
			delete props.audits;
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_AUDITS, props);
			dispatch({
				type: 'SET_STATE_DATA', payload: {
					[props?.result ? props?.result : 'audits']: [].concat(audits || []).concat(res.audits || []),
					moreAvailable: res.audits.length >= props.count,
				}
			});
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getAccount = useCallback(async ({ userID, block }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_GET(userID), {});

			if (block) {
				dispatch({ type: 'SET_STATE_DATA', payload: { loading: false, message: undefined } });
				return res;
			}
			// console.log(res?.user?.profile?.office?.address);
			dispatch({ type: 'LOAD_ACCOUNT', payload: res });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getTaskSummary = useCallback(async () => {
		try {
			dispatch({ type: 'IN_LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_SUMMARY_GET, {});
			// console.log(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_SUMMARY_GET, res, 1);
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const setPassword = useCallback(async ({ userID, password }) => {
		try {
			if (!userID || !password) {
				console.log('UserID is null')
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_PASSWORD_SET(userID), { password: password });
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			dispatch({ type: 'SET_STATE_DATA', payload: { passwordPanelVisible: undefined } });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const suspendAccount = useCallback(async ({ userID, reason, callback }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SUSPEND(userID), { suspendReason: reason });
			dispatch({ type: 'SET_STATE_DATA', payload: { suspendReasonPanelVisible: undefined } });
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const activateAccount = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_ACTIVATE(userID), {});
			getAccount({ userID });
		}
		catch (err) {
			if (['SAME_USER_EXISTS'].includes(err.response?.data?.message)) {
				dispatch({ type: 'LOADED' });
				return err.response?.data;
			}

			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const closeAccount = useCallback(async ({ userID, reason, callback }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_CLOSE(userID), { closeReason: reason });
			dispatch({ type: 'SET_STATE_DATA', payload: { suspendReasonPanelVisible: undefined } });
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const changeAccountType = useCallback(async ({ userID, callback }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_CHANGE_TYPE(userID), {});
			callback && callback();
			getAccount({ userID });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//----------------------------------------------------------------
	const sendOTP = useCallback(async ({ userID, mobile, ignoreSameContact, callback }) => {
		try {
			if (!userID || !mobile || !UtilityAPI.validateMobileNo(mobile)) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_ADD_MOBILE_SEND_OTP(userID, mobile), {
				ignoreSameContact: ignoreSameContact,
			});
			dispatch({ type: 'LOADED' });
			callback && callback();
		}
		catch (err) {
			// console.log(err?.response || err);
			if (err?.response?.status === 409) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { mobileNoCheckData: err.response.data } });
			}
			if (err + "" === "OTP_NOT_EXPIRED") {
				dispatch({ type: 'LOADED' });
				callback && callback();
			}
			else {
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
			}
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const addNewMobileNo = useCallback(async ({ userID, mobile, otpCode, callback }) => {
		try {
			throw new Error('Deprectated')
			// if (!userID || !mobile || !UtilityAPI.validateMobileNo(mobile)) {
			// 	return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			// }
			// dispatch({ type: 'LOADING' });
			// await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_ADD_MOBILE(userID), {
			// 	mobile: mobile,
			// 	otp: otpCode,
			// });
			// callback && callback();
			// getAccount({ userID });
		}
		catch (err) {
			if (['INVALID_OTP'].includes(err.response?.data?.message)) {
				dispatch({ type: 'SET_STATE_DATA', payload: { loading: false, message: false, requireCheck: true, otpError: err.response?.data?.message } });
				return;
			}
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [])

	//----------------------------------------------------------------
	const removeMobile = useCallback(async ({ userID, mobile, callback }) => {
		try {
			throw new Error('Deprectated')
			// if (!userID || !mobile) {
			// 	return;
			// }
			// dispatch({ type: 'LOADING' });
			// await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_REMOVE_MOBILE(userID, mobile), {});
			// callback && callback();
			// getAccount({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [])

	//----------------------------------------------------------------
	const setProfile = useCallback(async ({ userID, profile, username, callback }) => {
		try {
			if (!userID || !profile) {
				return;
			}
			// console.log(profile, username); return;
			const { fullname, mobiles, office, accountType } = profile;
			let { title, contact, phones, business } = office || {};
			const { name, mobile } = contact || {};
			const role = profile?.business?.profile?.agents?.length && profile?.business?.profile?.agents[0].role;

			phones = phones?.filter(c => c);
			let dataCheckList = accountType === 'ACCOUNT_TYPE_CUSTOMER' && !business ? [
				fullname,
				role === 'BUSINESS_ROLE_SYSTEM' || mobiles?.length,
			] : [];
			dataCheckList = accountType === 'ACCOUNT_TYPE_CUSTOMER' && business ? [
				fullname,
				mobiles?.length,
			] : dataCheckList;
			dataCheckList = accountType === 'ACCOUNT_TYPE_BUSINESS' ? [
				title,
				name,
				mobile,
				phones?.length,
			] : dataCheckList;
			dataCheckList = accountType === 'ACCOUNT_TYPE_OWNER' ? [
				fullname,
				mobiles?.length,
			] : dataCheckList;
			if (dataCheckList.some(c => !c)) {
				console.log('error in profile', business, profile, dataCheckList);
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: 'Missing fields', type: 'danger' }) });
				dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
				return;
			}

			dispatch({ type: 'LOADING' });
			delete profile.notes;
			delete profile.authenticate;
			delete profile.agents;


			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SET_PROFILE(userID), { profile });
			callback && callback();
			getAccount({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount])

	//----------------------------------------------------------------
	const createBusinessAccount = useCallback(async ({ userID, profile, callback }) => {
		try {
			if (!userID || !profile) {
				return;
			}
			const { office } = profile;
			let { title, contact, phones } = office || {};
			const { name, mobile } = contact || {};
			let dataCheckList = [
				title,
				name,
				mobile,
				phones?.length,
			];
			if (dataCheckList.some(c => !c)) {
				console.log(dataCheckList);
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: 'Missing fields', type: 'danger' }) });
				dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true, sameAccounts: undefined } });
				return;
			}

			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_CREATE_BUSINESS_ACCOUNT(userID), {
				profile: profile,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: { sameAccounts: undefined } });
			callback && callback();
		}
		catch (err) {
			dispatch({ type: 'SET_STATE_DATA', payload: { sameAccounts: undefined } });
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const detachingFromBusiness = useCallback(async ({ agentUserID, businessID }) => {
		try {
			if (!agentUserID || !businessID) {
				return;
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_DETACHING_FROM_BUSINESS(agentUserID, businessID), {});
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
			return err;
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const getSameBusinessAccounts = useCallback(async ({ user }) => {
		try {
			if (!user) {
				return;
			}
			user.profile = user.profile || {};
			const { office } = user.profile || {};
			let { title, contact, phones, addresses } = office || {};
			const { name, mobile } = contact || {};
			let dataCheckList = [
				title,
				name,
				mobile,
				phones?.length,
				addresses?.filter(address =>
					[
						address?.detail,
						address?.plaque,
						address?.unit,
						address?.location?.latitude,
						address?.location?.longitude,
					].some(c => !c)).length === 0
			];
			if (dataCheckList.some(c => !c)) {
				console.log(dataCheckList);
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: 'Missing fields', type: 'danger' }) });
				dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true, sameAccounts: undefined } });
				return;
			}

			// console.log(user?.profile);
			dispatch({ type: 'LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SAME_BUSINESS, {
				title: user.profile?.office?.title,
				latitude: user.profile?.office?.addresses[0]?.location?.latitude,
				longitude: user.profile?.office?.addresses[0]?.location?.longitude,
			});
			// console.log(res);
			dispatch({ type: 'SET_SAME_ACCOUNTS', payload: res.users.filter(ac => ac._id !== user._id) });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const setNote = useCallback(async ({ userID, note, callback }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SET_NOTE(userID, note?._id), {
				note: note,
			});
			callback && callback();
			await getAccount({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount])

	//----------------------------------------------------------------
	const getTotalFee = useCallback(({ monthlyFee, months }) => {
		let totalFee = 0;
		totalFee = months < 3 ? months * monthlyFee : totalFee;
		totalFee = months >= 3 && months < 6 ? months * monthlyFee * 0.95 : totalFee;
		totalFee = months >= 6 && months <= 11 ? months * monthlyFee * 0.9 : totalFee;
		totalFee = months > 11 ? months * monthlyFee * 0.8 : totalFee;
		return totalFee || 0;
	}, []);

	//----------------------------------------------------------------
	const getFilingServices = useCallback(async ({ userID, serviceID, conds, result }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'LOADING' });
			!result && dispatch({ type: 'SET_STATE_DATA', payload: { activateService: undefined, services: undefined, defaultService: undefined } });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_FILING_GET(userID, serviceID), conds);
			!result && dispatch({
				type: 'SET_STATE_DATA', payload: {
					loading: false,
					activateService: undefined,
					...res,

				}

			});
			// console.log(res);
			result && dispatch({ type: 'SET_STATE_DATA', payload: { [result]: { ...res }, loading: false } });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//-----------------------------------------------
	const getJetServices = useCallback(async ({ userID, serviceID, conds }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'LOADING' });
			dispatch({ type: 'SET_STATE_DATA', payload: { activateService: undefined, services: [], defaultService: undefined } });
			// console.log(0);
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_JET_GET(userID, serviceID), conds);
			dispatch({
				type: 'SET_STATE_DATA', payload: {
					loading: false,
					jetServices: res?.services,
				}
			});
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//-----------------------------------------------
	const deleteJetService = useCallback(async ({ userID, serviceID }) => {
		try {
			if (!userID || !serviceID) {
				return;
			}
			dispatch({ type: 'LOADING' });

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_JET_DELETE(userID, serviceID));
			getJetServices({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getJetServices])

	//----------------------------------------------------------------
	const cancelService = useCallback(async ({ userID, serviceID, cancelReason, cancelNote, callback }) => {
		try {
			if (!userID || !serviceID || !cancelReason) {
				// console.log(userID, serviceID, cancelReason)
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_CANCEL(userID, serviceID), {
				cancelReason: cancelReason,
				cancelNote: cancelNote,
			});
			callback && callback();
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			getFilingServices({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getFilingServices])

	//----------------------------------------------------------------
	const cancelDefaultService = useCallback(async ({ userID, callback }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_CANCEL_DEFAULT(userID), {});
			callback && callback();
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			getFilingServices({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getFilingServices])

	//----------------------------------------------------------------
	const activateService = useCallback(async ({ userID, serviceID }) => {
		try {
			if (!userID || !serviceID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_ACTIVATE(userID, serviceID), {});
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			getFilingServices({ userID });
		}
		catch (err) {
			if (['SERVICE_CONFLICT'].includes(err.response?.data?.message)) {
				dispatch({ type: 'SET_STATE_DATA', payload: { loading: false, message: false } });
				return err.response?.data;
			}
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getFilingServices])

	//----------------------------------------------------------------
	const setFilingService = useCallback(async ({ userID, serviceID, isDefaultService, isFreeService, service, setDefault, callback }) => {
		try {
			service = service || {};
			const {
				// registerType,
				// sType,
				officeAccess,
				monthlyFee,
				start,
				months,
				areas,
				freeDays,
				penaltyDays,
			} = service || {};

			const fields = [
				{ cond: officeAccess === true || officeAccess === false, field: 'officeAccess', title: 'دسترسی دفتری' },
				{ cond: isFreeService || monthlyFee, field: 'monthlyFee', title: 'مبلغ ماهانه' },
				{ cond: !isFreeService || (isFreeService && freeDays > 0), field: 'freeDays', errText: 'روزهای رایگان' },
				{ cond: isFreeService || !penaltyDays || (penaltyDays > 0 && penaltyDays <= 10), field: 'penaltyDays', errText: 'حداکثر روزهای جریمه 10 روز است.' },
				{ cond: isFreeService || (!isFreeService && freeDays <= 30) || isDefaultService, field: 'freeDays', errText: 'حداکثر روزهای رایگان 30 روز است.' },
				{ cond: isDefaultService || start, field: 'start', title: 'تاریخ شروع ' },
				{ cond: isDefaultService || isFreeService || months > 0, field: 'months', title: 'دوره سرویس' },
				{ cond: areas?.length > 0, field: 'areas', title: 'محله' },
			];
			// console.log(isFreeService, freeDays, !isFreeService || (isFreeService && freeDays > 0));
			let messages = fields.filter(fi => !fi.cond).map(fi => ({ msg: (fi.errText ? fi.errText : `${fi.title} را وارد کنید.`), focusInput: fi.field }));

			service.sType = (isDefaultService && 'SERVICE_TYPE_DEFAULT') || (isFreeService && 'SERVICE_TYPE_FREE') || 'SERVICE_TYPE_PAID';
			if (!userID || !service || messages.length > 0) {
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: 'Missing fields', type: 'danger' }) });
				dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true, messages: messages } });
				return;
			}
			dispatch({ type: 'LOADING' });
			if (setDefault) {
				service.totalFeeDefault = getTotalFee({ monthlyFee: service.monthlyFee, months: service.months });
			}
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_FILING_SET(userID, serviceID), {
				service,
				setDefault,
			});
			dispatch({ type: 'LOADED' });
			callback && callback();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getTotalFee])

	//----------------------------------------------------------------
	const setInvoice = useCallback(async ({ userID, invoice, callback }) => {
		try {
			invoice = invoice || {};
			const {
				// registerType,
				// sType,
				expireAt,
				amount,
				desc,
			} = invoice || {};

			if (!userID || !invoice || [
				expireAt,
				amount,
				desc,
			].some(c => !c)) {
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: 'Missing fields', type: 'danger' }) });
				return;
			}
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNTING_INVOICE_SET(userID, invoice?._id), {
				invoice: invoice,
			});
			callback && callback();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//--------------------------------------------
	const getInvoices = useCallback(async ({ userID }) => {
		try {
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNTING_INVOICES_GET(userID), {});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const addAgentToOffice = useCallback(async ({ userID, employeeUserID, access, callback, errorCallback }) => {
		try {
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_EMPLOYEE_ADD(userID), {
				employeeUserID,
				access: access,
			});
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			if (err.response?.data?.message === 'IS_IN_DEBT' && errorCallback) {
				return errorCallback(err.response?.data?.message);
			}
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const getTransactions = useCallback(async ({ userID, transactionID, defaultList }) => {
		try {
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNTING_USER_TRANSACTIONS_GET(userID, transactionID), {
				dateFrom: defaultList ? UtilityAPI.difDay(-90) : undefined,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getAccountMainHistory = useCallback(async ({ userID, first, count, filters }) => {
		try {
			filters = filters || {};
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICES_TRANSACTIONS_HISTORY_GET(userID), {
				first: first,
				count: count || 50,
				...filters,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getNetworkServices = useCallback(async ({ serviceID, first, count, filters }) => {
		try {
			filters = filters || {};
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_NETWORK_SERVICES_GET(serviceID), {
				first: first,
				count: count || 50,
				...filters,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const setTransaction = useCallback(async ({ userID, transaction, callback }) => {
		try {
			// console.log(transaction)
			let error = UtilityAPI.validateEntity({
				transaction,
				conditions: {
					userID: () => userID,
					reference: ({ reference }) => reference,
					date: ({ date }) => date,
					paymentMethod: ({ paymentMethod }) => paymentMethod,
					tType: ({ tType }) => tType,
					amount: ({ amount }) => amount > 0,
					desc: ({ desc }) => desc,
				}
			});

			if (error) {
				console.log(error);
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}

			dispatch({ type: 'SET_STATE_DATA', payload: { paymentSaving: true } });

			await asyncServiceCall(transaction._id ? Commands.API_URL_BITBITE_BO_ACCOUNTING_TRANSACTION_SET(userID, transaction._id) : Commands.API_URL_BITBITE_BO_ACCOUNTING_TRANSACTION_NEW(userID, transaction._id), {
				transaction: transaction,
			});
			//dispatch({ type: 'SET_STATE_DATA', payload: { paymentSaving: false } });
			getAccount({ userID });
			getTransactions({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			//dispatch({ type: 'SET_STATE_DATA', payload: { paymentSaving: false } });
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount, getTransactions]);

	//--------------------------------------------
	const newLedgerAdjustment = useCallback(async ({ userID, ledger, callback }) => {
		try {
			let error = UtilityAPI.validateEntity({
				ledger,
				conditions: {
					userID: () => userID,
					amount: ({ credit, debit }) => credit || debit,
					note: ({ note }) => note,
				}
			});

			if (error) {
				console.log(error);
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNTING_LEDGER_ADJUST(userID), { ledger });
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const getNextTasks = useCallback(async () => {
		try {
			dispatch({ type: 'IN_LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_NEXT_TASKS_GET, {});
			dispatch({
				type: 'LOAD_TASKS', payload: {
					...res,
					message: res.tasks.length ? undefined : Object.assign({}, { text: 'NO_MORE_SUPPORT_TASK', type: 'warning' })
				}
			});
			/* //test task assigment
			let list1 = res?.tasks.map(c => c._id);
			for (let i = 0; i < 100; i++) {
				console.log(i);
				let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_NEXT_TASKS_GET, {});
				dispatch({
					type: 'LOAD_TASKS', payload: {
						...res,
						message: res.tasks.length ? undefined : Object.assign({}, { text: 'NO_MORE_SUPPORT_TASK', type: 'warning' })
					}
				});
				let list2 = res?.tasks.map(c => c._id);
				if (list2.some(c => !list1.includes(c)) || list1.some(c => !list2.includes(c))) {
					console.log(list1, list2);
					break;
				}
			}//*/

			return res.tasks;
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const finishTasks = useCallback(async (taskID, callback) => {
		if (!taskID) {
			return;
		}
		try {
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_FINISH(taskID), {});
			dispatch({ type: 'LOAD_TASKS', payload: { tasks: [] } });
			callback && dispatch({ type: 'SET_STATE_DATA', payload: { user: undefined } });
			callback && setTimeout(() => callback(), 300);
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall]);

	// //--------------------------------------------
	// const loadMoreTasks_deprectaed = useCallback(async ({ taskLoadedLength, userID }) => {
	// 	if (!userID) {
	// 		return;
	// 	}
	// 	try {
	// 		dispatch({ type: 'IN_LOADING' });
	// 		let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_LIST_GET, {
	// 			first: taskLoadedLength || 0,
	// 			count: 5,
	// 			state: 'TASK_STATE_CLOSED',
	// 			userID: userID,
	// 		});
	// 		dispatch({
	// 			type: 'LOAD_MORE_TASK', payload: {
	// 				...res,
	// 				moreAvailable: res.tasks?.length >= 5,
	// 			}
	// 		});
	// 	}
	// 	catch (err) {
	// 		console.log(err);
	// 		dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

	// 	}
	// }, [asyncServiceCall]);

	//--------------------------------------------
	const getUserTasks = useCallback(async ({ userID, first, count, closed, setAvailable }) => {
		if (!userID) {
			return;
		}
		try {
			dispatch({ type: 'IN_LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_LIST_GET, {
				states: closed ? 'TASK_STATE_CLOSED,TASK_STATE_ARCHIVED' : 'TASK_STATE_NEW,TASK_STATE_OPEN',
				userID: userID,
				first: first,
				count: count,
			});

			setAvailable && dispatch({
				type: 'SET_STATE_DATA', payload: {
					loading: false,
					moreClosedAvailable: res.tasks?.length > 0,
				}
			});
			!setAvailable && closed && dispatch({
				type: 'LOAD_MORE_CLOSED_TASKS', payload: {
					...res,
					first: first + count,
					moreClosedAvailable: res.tasks?.length >= count,
				}
			});
			!setAvailable && !closed && dispatch({
				type: 'LOAD_TASKS', payload: {
					...res,
					first: first + count,
				}
			});
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const taskProcessDone = useCallback(async ({ taskID, debit, noNeeded, noAnswer, noRenewal, wait, other, done, note, date, closeModal }) => {
		try {
			dispatch({ type: 'IN_LOADING' });

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_DONE({ taskID, debit, noNeeded, noAnswer, noRenewal, wait, other, done }), {
				date: date, note: note
			});

			dispatch({ type: 'LOAD_TASK_PRCOESS_DONE', payload: { taskID: taskID } });
			closeModal && closeModal();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const taskReactive = useCallback(async ({ userID, taskID, callback }) => {
		try {

			if (!userID || !taskID) {
				console.log('userID || taskID is null')
				return;
			}
			dispatch({ type: 'IN_LOADING' });

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_DONE({ taskID, reactive: true }), {});

			dispatch({ type: 'SET_STATE_DATA', payload: { tasks: [] } });

			setTimeout(() => {
				getUserTasks({ userID, first: 0, count: 5 });
			}, 500);
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall, getUserTasks]);

	//--------------------------------------------
	const taskEditCallTime = useCallback(async ({ userID, taskID, date, note, callback }) => {
		try {
			if (!userID || !taskID || !date) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_TASK_DONE({ taskID, callTime: true }), { date, note });
			dispatch({ type: 'SET_STATE_DATA', payload: { tasks: [] } });

			setTimeout(() => {
				getUserTasks({ userID, first: 0, count: 5 });
			}, 500);
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall, getUserTasks]);

	//--------------------------------------------
	const setAuthentication = useCallback(async ({ userID, authenticateEntry }) => {
		try {
			dispatch({ type: 'IN_LOADING' });

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_AUTHENTICATION_SET(userID), {
				authenticateEntry: authenticateEntry,
			});
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			setTimeout(() => getAccount({ userID }), 2000);
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
			return err.response?.data?.message || err;

		}
	}, [asyncServiceCall, getAccount]);


	//--------------------------------------------
	const searchAccounts = useCallback(async ({ filters, pageNo, count }) => {
		// console.log(filters)
		try {
			dispatch({ type: 'IN_LOADING' });
			filters = filters || {};
			count = count || 50;

			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_ACCOUNTS, {
				...filters,
				first: pageNo * count,
				count: count,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
			return res;
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);


	//--------------------------------------------
	const saveAgentAccess = useCallback(async ({ userID, agentUserID, access, callback }) => {
		try {
			dispatch({ type: 'IN_LOADING' });
			access = access || {};
			Object.keys(access).forEach(c => {
				if (![
					'role',
					'officeAccess',
					'mobileAccess',
					'actions',
					'dTypes',
					'eTypes',
					'areas',
					'size',
					'priceTotal',
				].includes(c)) {
					delete access[c];
				}
			});

			await asyncServiceCall(Commands.API_URL_BITBITE_BO_FILING_SERVICE_EMPLOYEE_ACCESS_SET(userID, agentUserID), {
				access: access
			});
			dispatch({ type: 'LOADED' });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//----------------------------------------------------------
	const getAccounts = useCallback(async ({ filters, pageNo, count }) => {
		// console.log(filters)
		try {
			dispatch({ type: 'IN_LOADING' });
			filters = filters || {};
			count = count || 25;

			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_ACCOUNTS, {
				filters: filters,
				first: pageNo * count,
				count: count,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//----------------------------------------------------------------
	const getUserNetworkServices = useCallback(async ({ userID, serviceID, conds }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_NETWORK_GET(userID, serviceID), { ...conds });
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const cancelNetworkService = useCallback(async ({ userID, serviceID, cancelReason, cancelNote, callback }) => {
		try {
			if (!userID || !serviceID || !cancelReason) {
				console.log(userID, serviceID, cancelReason)
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_NETWORK_SERVICE_CANCEL(userID, serviceID), {
				cancelReason: cancelReason,
				cancelNote: cancelNote,
			});
			callback && callback();
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			getUserNetworkServices({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getUserNetworkServices])

	//----------------------------------------------------------------
	const setNetworkService = useCallback(async ({ userID, serviceID, task, callback }) => {
		try {
			task = task || {};

			let error = UtilityAPI.validateEntity({
				task,
				userID,
				conditions: {
					userID: () => userID,
					startAt: ({ startAt }) => startAt,
				}
			});

			if (error) {
				console.log(userID, error);
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: 'Missing fields', type: 'danger' }) });
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}

			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_NETWORK_SET(userID, serviceID), {
				task: task,
			});
			dispatch({ type: 'LOADED' });
			callback && callback();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])
	//----------------------------------------------------------------
	const getNetworkUsers = useCallback(async () => {
		try {
			dispatch({ type: 'LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SERVICE_NETWORK_USERS_GET, {});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//--------------------------------------------
	const networkTaskProcessDone = useCallback(async ({ taskID, done, noNeeded, wait, other, note, date, closeModal, calback }) => {
		try {
			dispatch({ type: 'IN_LOADING' });
			if (!taskID || (!done && !noNeeded && !wait && !other)) {
				return;
			}
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_NETWORK_TASK_DONE({ taskID, done, noNeeded, wait, other }), { date: date, note: note });

			dispatch({ type: 'LOAD_TASK_PRCOESS_DONE', payload: { taskID: taskID } });
			closeModal && closeModal();
			calback && calback();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });

		}
	}, [asyncServiceCall]);

	//----------------------------------------------------------------
	const getUserSessions = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_SESSIONS_GET(userID), {});

			dispatch({ type: 'LOADED' });
			dispatch({ type: 'SET_STATE_DATA', payload: res });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const closeUserSession = useCallback(async ({ businessUserID, userID, token }) => {
		try {
			if (!userID || !token) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_SESSION_DELETE(userID), { sessionToken: token });
			dispatch({ type: 'LOADED' });
			getUserSessions({ userID: businessUserID || userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getUserSessions])

	//----------------------------------------------------------------
	const closeUserSessions = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_SESSION_DELETE_ALL(userID));
			dispatch({ type: 'LOADED' });
			getUserSessions({ userID: userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getUserSessions])

	//--------------------------------------------
	const getBankAccounts = useCallback(async () => {
		try {
			dispatch({ type: 'IN_LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_ACCOUNTING_BANK_ACCOUNT_LIST_GET, {});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const setUserBankAccount = useCallback(async ({ userID, bankAccount, callback }) => {
		try {
			if (!userID || !bankAccount) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}

			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_BANK_ACCOUNT_SET(userID), {
				bankAccountID: bankAccount._id,
			});
			dispatch({ type: 'LOADED' });
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const setUserTaxProfile = useCallback(async ({ userID, taxProfile, callback }) => {
		try {
			if ([
				!userID,
				!taxProfile,
				!taxProfile?.legalType,
				!taxProfile?.fullname,
				!taxProfile?.nationalID,
				taxProfile?.legalType === 'TAX_LEGAL_TYPE_BUSINESS' && !taxProfile?.economicCode,
				!taxProfile?.invoiceType,
				!taxProfile?.source,
				(taxProfile?.legalType === 'TAX_LEGAL_TYPE_BUSINESS' || taxProfile?.invoiceType === 'TAX_INVOICE_TYPE_FORMAL')
				&& !taxProfile?.address,
				(taxProfile?.legalType === 'TAX_LEGAL_TYPE_BUSINESS' || taxProfile?.invoiceType === 'TAX_INVOICE_TYPE_FORMAL')
				&& !taxProfile?.tel,
				(taxProfile?.legalType === 'TAX_LEGAL_TYPE_BUSINESS' || taxProfile?.invoiceType === 'TAX_INVOICE_TYPE_FORMAL')
				&& !taxProfile?.postalCode,
			].some(c => c)) {
				console.log(taxProfile);
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}

			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_TAX_PROFILE_SET(userID), {
				taxProfile,
			});
			dispatch({ type: 'LOADED' });
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const removeUserTaxProfile = useCallback(async ({ userID, callback }) => {
		try {
			if ([
				!userID,
			].some(c => c)) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}

			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_TAX_PROFILE_DELETE(userID));
			dispatch({ type: 'LOADED' });
			getAccount({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount]);

	//--------------------------------------------
	const loadRandomTaxProfile = useCallback(async () => {
		try {
			dispatch({ type: 'IN_LOADING' });
			const taxProfile = await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_TAX_PROFILE_RANDOM);
			dispatch({ type: 'SET_STATE_DATA', payload: { taxProfile } });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getServiceLogs = useCallback(async ({ userID, type, first, count, filters }) => {
		try {
			filters = filters || {};
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SERVICES_LOGS_GET(userID), type !== 'SERVICE_LOG_ALL' ? {
				type: type, first, count, ...filters
			} : { first, count, ...filters });
			dispatch({ type: 'SET_STATE_DATA', payload: { logs: res.list, total: res.total } });
		}
		catch (err) {
			console.log(err?.response)
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//---------------------------------------------------------------------------
	const getServiceLogDetail = useCallback(async ({ userID, logID }) => {
		try {
			dispatch({ type: 'SET_STATE_DATA', payload: { logDetail: undefined, loading: true, message: undefined } });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SERVICES_LOG_DETAIL_GET(userID, logID));
			dispatch({ type: 'SET_STATE_DATA', payload: { logDetail: res, loading: false } });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//---------------------------------------------------------------------------
	const clearCounters = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_CLEAR_COUNTERS(userID));
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'عملیات با موفقیت انجام شد.', type: 'success' }) });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//---------------------------------------------------------------------------
	const getContactMessages = useCallback(async ({ pageNo }) => {
		try {
			dispatch({ type: 'LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_CONTACT_US_GET, {
				currentPage: pageNo || 0,
			});
			dispatch({ type: 'SET_STATE_DATA', payload: { ...res, loading: false } });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//---------------------------------------------------------------------------
	const contactMessagesCecked = useCallback(async ({ _id, checked, onSuccess }) => {
		try {
			if (!_id) {
				return;
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_CONTACT_US_SET_CHECKED(_id, checked));
			dispatch({ type: 'LOADED' });
			onSuccess && onSuccess();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//---------------------------------------------------------------------------
	const createOfficeSystemAccount = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_OFFICE_SYSTEM_ACCOUNT_CREATE(userID));
			// dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'عملیات با موفقیت انجام شد.', type: 'success' }) });
			dispatch({ type: 'LOADED' });
			return res;
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
			return null;
		}
	}, [asyncServiceCall])




	//---------------------------------------------------------------------------
	const checkForServiceConflict = useCallback(async ({ userID, service }) => {
		try {
			if (!userID || !service) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_SUPPORT_SERVICE_CONFILICT_CHECK(userID), {
				serviceID: service._id,
				start: service.start,
				end: service.end,
			});
			dispatch({ type: 'LOADED' });
			return res;
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
			return null;
		}
	}, [asyncServiceCall])


	//----------------------------------------------------------------
	const getServiceDays = useCallback(({ start, months, isFreeService }) => {
		if (!start || !months || isFreeService) {
			// !isFreeService && console.log('zero months:', start, months);
			return 0;
		}

		// let days = new Date(start).getHours() < 10 ? -1 : 0;
		let days = 0;
		// start = new Date(start).getHours() < 10 ? moment(start) : moment(start).add(1, 'day');
		// console.log(start.format("jYYYY/jMM/jDD HH:mm:ss"), months);
		start = moment(start);

		days += start.jMonth() + 1 === 6 && start.jDate() === 31 && new Date(start.valueOf()).getHours() >= 10 ? -1 : 0; // 6/31 => 30 days
		for (let i = 1; i <= months; i++) {
			days += start.jMonth() + 1 <= 6 ? 31 : 30;
			start = start.add(start.jMonth() + 1 <= 6 ? 31 : 30, 'day');
		}
		return days;
	}, []);

	//----------------------------------------------------------------
	const getServiceActiveDays = useCallback(({ start, steps, maxDate }) => {
		if (!steps?.length || new Date(steps[steps?.length - 1]?.start) > new Date()) {
			return 0;
		}

		// console.log('getServiceActiveDays ===========>0', steps.map(c => `${c.state}:${moment(c.start).format("jYYYY/jMM/jDD HH:mm:ss")} `));
		//ignore steps before start cause service register is not the same as start
		//ignore steps before start cause service register is not the same as start
		if (steps.some(c => new Date(c.start) < new Date(start))) {
			let index = steps.findIndex(c => new Date(c.start) < new Date(start));
			//Replace one step for all steps before start
			//if there isn't any step after start, new step is added in next codes so no step is replaced here
			if (index >= 0 && steps.filter(c => new Date(c.start) >= new Date(start)).length) {
				steps[index].start = start;
			}
		}

		let steps0 = steps[0];
		steps = steps.filter(c => new Date(c.start) >= new Date(start));
		let days = 0;

		if (!steps.length && steps0.state === 'SERVICE_STATE_ACTIVE' && new Date() > new Date(start)) { //normal service or stopped/started service before service start
			steps = steps.concat([{ start: start, state: 'SERVICE_STATE_ACTIVE' }]);
			// steps.push({ start: start, state: 'SERVICE_STATE_ACTIVE' });
		}
		else if (steps.length && steps[steps.length - 1]?.state !== 'SERVICE_STATE_ACTIVE') { //stopped service after start 
			steps = steps.concat([{ start: start, state: 'SERVICE_STATE_ACTIVE' }]);
		}

		// console.log('getServiceActiveDays ===========>1', steps.map(c => `${c.state}:${moment(c.start).format("jYYYY/jMM/jDD HH:mm:ss")} `));
		for (let i = steps.length - 1; i >= 0; i--) {
			if (steps[i].state !== 'SERVICE_STATE_ACTIVE') {
				continue;
			}
			let a = moment(steps[i].start);
			let b = moment(i > 0 ? steps[i - 1].start : (maxDate || new Date()));
			if (a.valueOf() === b.valueOf()) {
				continue;
			}

			a = a.add(new Date(a.valueOf()).getHours() >= 10 && a.jMonth() + 1 !== 12 ? 1 : 0, 'days').startOf('day');
			b = b.add(new Date(b.valueOf()).getHours() >= 14 ? 1 : 0, 'days').startOf('day');
			let difDays = b.diff(a, 'days');
			days += difDays > 0 ? difDays : 0;
			// console.log('getServiceActiveDays ===========>2', moment(a).format("jYYYY/jMM/jDD HH:mm:ss"), moment(b).format("jYYYY/jMM/jDD HH:mm:ss"), difDays, days);
		}
		// console.log('getServiceActiveDays===========>3', days);
		return days;
	}, []);

	//----------------------------------------------
	const setServiceEnd = useCallback(({ service }) => {
		if (service.sType === 'SERVICE_TYPE_DEFAULT') {
			return;
		}
		if (service.steps && service.steps[0]?.state !== 'SERVICE_STATE_ACTIVE') {
			service.end = new Date();
			return;
		}

		service.days = service.sType === 'SERVICE_TYPE_FREE' ? 0 : getServiceDays({ start: service.start, months: service.months });
		// console.log('setServiceEnd ===========>', service.serviceNo, service.steps.length, service.steps.map(c => moment(c.start).format("jYYYY/jMM/jDD HH:mm:ss"))); //return;

		let activeDays = getServiceActiveDays({ start: service.start, steps: service.steps, serviceNo: service.serviceNo });

		const start = !service.steps?.length || new Date(service.start) >= new Date() ? service.start : service.steps[0].start;


		service.end = moment(start).add(
			(service.days || 0)
			+ (service.freeDays || 0)
			- (service.penaltyDays || 0)
			- (activeDays || 0)
			+ ((moment(start).jMonth() + 1 !== 12) && new Date(start).getHours() >= 10 ? 0 : -1),
			'day').set({ hour: 23, minute: 59, second: 58, millisecond: 0 }).valueOf();

		/*
		console.log(
			moment(service.start).format("jYYYY/jMM/jDD HH:mm:ss"),
			moment(service.steps[0].start).format("jYYYY/jMM/jDD HH:mm:ss"),
			moment(start).format("jYYYY/jMM/jDD HH:mm:ss"),
			moment(service.end).format("jYYYY/jMM/jDD HH:mm:ss"),
			service.days,
			service.freeDays,
			activeDays,
		);//*/


	}, [getServiceDays, getServiceActiveDays]);


	//--------------------------------------------------
	const UpdateFilingService = useCallback(({
		serviceID,
		service,
		services,
		isDefaultService,
		isFreeService,
		isPaidService,
	}) => {

		// console.log('1', service.start);
		// service.steps?.length && console.log('==>-1', service.steps.length, moment(service.start).format("jYYYY/jMM/jDD HH:mm:ss"), service.steps.map(c => moment(c.start).format("jYYYY/jMM/jDD HH:mm:ss"))); return;

		service = service ? { ...service } : {};
		services = services || [];

		const _start = service.start;

		if (!serviceID && !service.sType) {
			service.title = i18n(UtilityAPI.classBuilder({
				'SERVICE_TYPE_DEFAULT': isDefaultService,
				'SERVICE_TYPE_FREE': isFreeService,
				'SERVICE_TYPE_PAID': isPaidService,
			}));
			service.start = service.start || new Date().getTime();
			service.maxIP = 1;
			service.maxMobileStations = 1;
			service.freeDays = 0;
			service.penaltyDays = 0;
			service.months = isFreeService ? 0 : 1;
			service.sType = UtilityAPI.classBuilder({
				'SERVICE_TYPE_DEFAULT': isDefaultService,
				'SERVICE_TYPE_FREE': isFreeService,
				'SERVICE_TYPE_PAID': isPaidService,
			});
		}

		if (!serviceID) {
			delete service.userID;
			delete service.registerType;
			delete service.state;
			delete service.steps;
			delete service.end;
		}

		if (!serviceID) {
			services = services.filter(s => s.sType !== 'SERVICE_TYPE_DEFAULT');
			let maxEnd = Math.max(...(services.filter(s => s.state === 'SERVICE_STATE_ACTIVE').map(c => new Date(c.end).getTime())));
			let activeService = services.find(c => new Date(c.end).getTime() === maxEnd && new Date(c.end) > new Date());
			if (activeService?.end) {
				var t = new Date(activeService.end);
				t.setSeconds(t.getSeconds() + 1);
				service.start = t.getTime();
			}
			else {
				service.start = new Date().getHours() < 10 ? moment().subtract(1, 'days').set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).valueOf() : new Date().getTime();
			}
		}
		service.state = 'SERVICE_STATE_ACTIVE';
		delete service.registerType;

		if (!serviceID) {
			service.sType = service.sType || 'SERVICE_TYPE_PAID';
			service.state = 'SERVICE_STATE_ACTIVE';
			service.steps = [{ start: new Date(), state: 'SERVICE_STATE_ACTIVE' }];
			delete service._id;
		}
		else {
			let service0 = services?.find(c => c._id === serviceID);
			if (!service0) {
				// console.log(`SERVICE_NOT_FOUND[${serviceID}]`);
				return;
			}
			service0 = JSON.parse(JSON.stringify({ steps: service0.steps, start: service0.start, state: service0.state }));

			delete service.userID;
			delete service.sType;
			delete service.registerType;
			delete service.state;
			delete service.steps;

			if (!(new Date(service.start).getTime() && service0.start !== service.start && new Date(service0.start) > new Date() && new moment(service.start).valueOf() >= new moment(service.start).startOf('day').valueOf())) {
				// console.log(`start deleted`);
				delete service.start;
			}

			// console.log('==>-1', service0.steps.length, moment(service0.start).format("jYYYY/jMM/jDD HH:mm:ss"), service0.steps.map(c => moment(c.start).format("jYYYY/jMM/jDD HH:mm:ss")), service.steps); //return;
			if (serviceID && new Date(service0.start) < new Date()) {
				// console.log('===========>serviceID', serviceID);
				service0.steps = [{
					start: new Date().getHours() < 14 && moment().startOf('day').valueOf() === moment(service0.steps[0].start).startOf('day').valueOf() ? service0.steps[0].start : moment().add(new Date().getHours() >= 14 ? 1 : 0, 'days').startOf('day'),
					state: service0.state
				}].concat(service0.steps);
				// delete service.steps;
			}
			// console.log('==>-1', service0.steps.length, service0.steps.map(c => moment(c.start).format("jYYYY/jMM/jDD HH:mm:ss")));

			service = JSON.parse(JSON.stringify({
				...service0,
				...service,
			}));
		}

		if (service.sType === 'SERVICE_TYPE_FREE') {
			service.monthlyFee = 0;
			service.totalFee = 0;
		}


		setServiceEnd({ service });
		if (_start && (service.start - _start < 1000)) {
			service.start = _start; //prevent from looping on start update
		}
		dispatch({ type: 'SET_EDIT_SERVICE', payload: { ...service } });
		// console.log(2, service);

	}, [setServiceEnd, dispatch, i18n]);

	//--------------------------------------------
	const rejectTransaction = useCallback(async ({ userID, transactionID, callback }) => {
		if (!transactionID || !userID) {
			return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
		}
		try {
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNTING_TRANSACTION_REJECT(transactionID), {});
			dispatch({ type: 'LOADED' });
			getAccount({ userID });
			getTransactions({ userID });
			callback && callback();
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [getAccount, getTransactions, asyncServiceCall]);

	//--------------------------------------------
	const newAuditLog = useCallback(async ({ what, action, userID }) => {
		if (!what || !action) {
			console.error('missing params');
			return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
		}
		try {
			dispatch({ type: 'IN_LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_AUDIT_LOG_NEW, { what, action, userID });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------------------
	const getMenus = useCallback(async () => {
		try {
			dispatch({ type: 'LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_MENU_LIST_GET, {});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });

		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [dispatch, asyncServiceCall]);

	//--------------------------------------------------------
	const getOrders = useCallback(async ({ userID }) => {
		try {
			dispatch({ type: 'LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_ORDER_LIST_GET(userID));
			dispatch({ type: 'LOADED' });
			dispatch({ type: 'SET_STATE_DATA', payload: res });

		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [dispatch, asyncServiceCall]);

	//--------------------------------------------------------
	const downloadOrders = useCallback(async ({ userID }) => {
		try {
			dispatch({ type: 'LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_ORDER_LIST_GET(userID), { download: 1 });
			dispatch({ type: 'LOADED' });
			let csvContent = "data:text/csv;charset=utf-8,\uFEFF" + res;
			var encodedUri = encodeURI(csvContent);

			var link = document.createElement("a");
			link.setAttribute("href", encodedUri);
			link.setAttribute("download", "orders.csv");
			document.body.appendChild(link);
			link.click();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [dispatch, asyncServiceCall]);

	return (
		<AccountContext.Provider
			value={{
				state,
				...state,
				dispatch,
				getAccount,
				setPassword,
				suspendAccount,
				activateAccount,
				changeAccountType,
				sendOTP,
				addNewMobileNo,
				removeMobile,
				setProfile,
				getProvinces,
				getAudits,
				createBusinessAccount,
				detachingFromBusiness,
				getSameBusinessAccounts,
				setNote,
				getFilingServices,
				setFilingService,
				cancelService,
				activateService,
				setInvoice,
				getInvoices,
				addAgentToOffice,
				getTransactions,
				setTransaction,
				getNextTasks,
				finishTasks,
				taskProcessDone,
				getTaskSummary,
				setAuthentication,
				getServiceDays,
				getTotalFee,
				searchAccounts,
				saveAgentAccess,
				getAccountMainHistory,
				getAccounts,
				getUserNetworkServices,
				setNetworkService,
				cancelNetworkService,
				getNetworkServices,
				getNetworkUsers,
				networkTaskProcessDone,
				getUserSessions,
				closeUserSession,
				closeUserSessions,
				getUserTasks,
				taskReactive,
				taskEditCallTime,
				getBankAccounts,
				setUserBankAccount,
				closeAccount,
				getServiceLogs,
				getServiceLogDetail,
				cancelDefaultService,
				clearCounters,
				newLedgerAdjustment,
				createOfficeSystemAccount,
				UpdateFilingService,
				checkForServiceConflict,
				getContactMessages,
				contactMessagesCecked,
				setServiceEnd,
				getServiceActiveDays,
				rejectTransaction,
				getJetServices,
				deleteJetService,
				newAuditLog,
				setUserTaxProfile,
				loadRandomTaxProfile,
				removeUserTaxProfile,
				getMenus,
				getOrders,
				downloadOrders,
			}}
		>
			{children}
		</AccountContext.Provider>
	)
}

export default AccountState
